import { Check, ChevronDown, Loader2, Search, X } from "lucide-react"
import {
  type KeyboardEvent,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react"
import { Badge } from "@/components/ui/badge"
import { Label } from "@/components/ui/label"
import { useTagSearch } from "@/features/tags/hooks/useTagSearch"
import { cn } from "@/lib"

interface TagsInputProps {
  id: string
  label: string
  placeholder?: string
  tags: string[]
  onAddTag: (tag: string) => void
  onRemoveTag: (tag: string) => void
  error?: string
  onClearError?: () => void
  disabled?: boolean
  required?: boolean

  // New props (optional, for search feature)
  enableSearch?: boolean // Enable dropdown search
  entityId?: string // Current entity ID (to exclude attached tags)
}

/**
 * TagSearchDropdown - Internal component for search dropdown
 */
interface TagSearchDropdownProps {
  inputValue: string
  onSelect: (tagText: string) => void
  onClose: () => void
  inputRef: RefObject<HTMLInputElement | null>
  excludeIds: string[]
  containerRef: RefObject<HTMLDivElement | null>
}

function TagSearchDropdown({
  inputValue,
  onSelect,
  onClose,
  inputRef,
  excludeIds,
  containerRef,
}: TagSearchDropdownProps) {
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number
    left: number
    width: number
  } | null>(null)

  const { tags, isLoading, hasMore, error, setSearchQuery, loadMore } = useTagSearch({
    enabled: true,
    pageSize: 20,
    debounceMs: 300,
    excludeIds,
  })

  const listRef = useRef<HTMLUListElement>(null)
  const itemRefs = useRef<(HTMLLIElement | null)[]>([])

  // Update search query when input value changes
  useEffect(() => {
    setSearchQuery(inputValue)
  }, [inputValue, setSearchQuery])

  // Position dropdown below input
  useLayoutEffect(() => {
    if (containerRef.current && inputRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect()
      const inputRect = inputRef.current.getBoundingClientRect()

      setDropdownPosition({
        top: inputRect.bottom - containerRect.top + 4,
        left: inputRect.left - containerRect.left,
        width: inputRect.width,
      })
    }
  }, [containerRef, inputRef])

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault()
          setFocusedIndex(prev => (prev < tags.length - 1 ? prev + 1 : prev))
          break
        case "ArrowUp":
          e.preventDefault()
          setFocusedIndex(prev => (prev > 0 ? prev - 1 : -1))
          break
        case "Enter":
          e.preventDefault()
          if (focusedIndex >= 0 && tags[focusedIndex]) {
            onSelect(tags[focusedIndex].tagText)
            setFocusedIndex(-1)
          } else if (inputValue.trim()) {
            // Create new tag if no tag is selected
            onSelect(inputValue.trim())
          }
          break
        case "Escape":
          e.preventDefault()
          onClose()
          break
      }
    },
    [focusedIndex, tags, inputValue, onSelect, onClose],
  )

  // Scroll focused item into view
  useEffect(() => {
    if (focusedIndex >= 0 && itemRefs.current[focusedIndex]) {
      itemRefs.current[focusedIndex]?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      })
    }
  }, [focusedIndex])

  // Infinite scroll handler
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLUListElement>) => {
      const target = e.target as HTMLUListElement
      const scrollBottom = target.scrollHeight - target.scrollTop - target.clientHeight

      if (scrollBottom < 100 && hasMore && !isLoading) {
        loadMore()
      }
    },
    [hasMore, isLoading, loadMore],
  )

  // Register keyboard handler
  useEffect(() => {
    const input = inputRef.current
    if (input) {
      input.addEventListener("keydown", handleKeyDown as unknown as EventListener)
      return () => {
        input.removeEventListener("keydown", handleKeyDown as unknown as EventListener)
      }
    }
  }, [handleKeyDown, inputRef])

  // Clear focus when tags change
  useEffect(() => {
    setFocusedIndex(-1)
  }, [])

  if (!dropdownPosition) return null

  const showTags = tags.length > 0
  const showEmpty = inputValue.trim() && tags.length === 0 && !isLoading
  const showError = error !== null

  return (
    <div
      className="absolute z-50 bg-popover border rounded-md shadow-lg overflow-hidden"
      style={{
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        width: dropdownPosition.width,
        maxHeight: "300px",
      }}
    >
      {/* Loading skeleton */}
      {isLoading && tags.length === 0 && (
        <div className="p-4 space-y-2">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="h-10 bg-muted rounded animate-pulse"
              style={{ animationDelay: `${i * 100}ms` }}
            />
          ))}
        </div>
      )}

      {/* Tags header */}
      {showTags && !isLoading && (
        <div className="px-3 py-2 text-xs font-medium text-muted-foreground border-b flex items-center gap-2">
          <Search className="size-3" />
          <span>
            {inputValue.trim() ? `Search results for "${inputValue}"` : "Available tags"}
            {isLoading && <Loader2 className="size-3 animate-spin ml-2" />}
          </span>
        </div>
      )}

      {/* Tag list */}
      {showTags && (
        <ul ref={listRef} className="max-h-[200px] overflow-y-auto" onScroll={handleScroll}>
          {tags.map((tag, index) => (
            <li
              key={tag.id}
              ref={el => {
                itemRefs.current[index] = el
              }}
              className={cn(
                "px-3 py-2 cursor-pointer flex items-center justify-between gap-1 transition-colors",
                focusedIndex === index ? "bg-accent" : "hover:bg-muted/50",
              )}
              onMouseDown={e => {
                e.preventDefault() // Prevents input blur during click
                onSelect(tag.tagText)
              }}
              onMouseEnter={() => setFocusedIndex(index)}
            >
              <span className="truncate flex-1 text-[11px]">{tag.tagText}</span>
              {focusedIndex === index && <Check className="size-4 text-primary shrink-0" />}
            </li>
          ))}

          {/* Loading more indicator */}
          {isLoading && tags.length > 0 && (
            <li className="px-3 py-2 flex items-center justify-center">
              <Loader2 className="size-4 animate-spin text-muted-foreground" />
            </li>
          )}
        </ul>
      )}

      {/* Empty state */}
      {showEmpty && !showError && (
        <div className="p-4 text-center text-sm text-muted-foreground">
          No tags found. Press <kbd className="px-1 py-0.5 bg-muted rounded text-xs">Enter</kbd> to
          add "{inputValue}" as a new tag
        </div>
      )}

      {/* Error state */}
      {showError && <div className="p-4 text-center text-sm text-destructive">{error}</div>}
    </div>
  )
}

/**
 * TagsInput Component
 *
 * Enhanced tag input with optional search dropdown support.
 *
 * When enableSearch is true:
 * - Shows recent user tags on empty input focus
 * - Shows search results with partial matching when typing
 * - Supports infinite scroll for large result sets
 * - Visual distinction between system tags (blue) and user tags (gray)
 * - Keyboard navigation (arrows, enter, escape)
 *
 * When enableSearch is false (default):
 * - Simple text input for manual tag entry
 * - Tags as badges with X buttons
 * - Keyboard shortcuts (Enter, Backspace, Escape)
 */
export function TagsInput({
  id,
  label,
  tags,
  onAddTag,
  onRemoveTag,
  error,
  onClearError,
  placeholder,
  disabled = false,
  required = false,
  enableSearch = false,
  // @ts-expect-error - Reserved for future use
  _entityId, // Reserved for future use: to exclude entity's current tags from search
}: TagsInputProps) {
  const [inputValue, setInputValue] = useState("")
  const [showDropdown, setShowDropdown] = useState(false)
  const [duplicateError, setDuplicateError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const skipDropdownOpenRef = useRef(false)

  // Get selected tag IDs to exclude from search
  const excludeIds = useRef<string[]>([])

  const handleFocus = () => {
    if (disabled) return
    if (error && onClearError) {
      onClearError()
    }
    // Skip opening dropdown if we just selected a tag
    if (skipDropdownOpenRef.current) {
      skipDropdownOpenRef.current = false
      return
    }
    if (enableSearch) {
      setShowDropdown(true)
    }
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Don't close dropdown if clicking inside it
    const relatedTarget = e.relatedTarget as HTMLElement
    const dropdown = containerRef.current?.querySelector('[role="dropdown"]')

    if (dropdown?.contains(relatedTarget)) {
      return
    }

    if (inputValue.trim()) {
      const trimmedValue = inputValue.trim()
      if (!tags.includes(trimmedValue)) {
        onAddTag(trimmedValue)
      } else {
        setDuplicateError(`"${trimmedValue}" is already added`)
        setTimeout(() => setDuplicateError(null), 3000)
      }
      setInputValue("")
    }

    setShowDropdown(false)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return
    setInputValue(e.target.value)
    if (error && onClearError) {
      onClearError()
    }
    if (duplicateError) {
      setDuplicateError(null) // Clear duplicate error when user starts typing
    }
    if (enableSearch) {
      setShowDropdown(true)
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return

    // Only handle these keys if dropdown is not shown
    if (!showDropdown) {
      switch (e.key) {
        case "Enter": {
          e.preventDefault()
          const trimmedValue = inputValue.trim()
          if (trimmedValue && !tags.includes(trimmedValue)) {
            onAddTag(trimmedValue)
            setInputValue("")
          }
          break
        }

        case "Backspace":
          if (!inputValue && tags.length > 0) {
            e.preventDefault()
            onRemoveTag(tags[tags.length - 1])
          }
          break

        case "Escape":
          e.preventDefault()
          setInputValue("")
          inputRef.current?.blur()
          break
      }
    }
  }

  const handleTagRemove = (tagToRemove: string) => {
    onRemoveTag(tagToRemove)
    setTimeout(() => {
      inputRef.current?.focus()
    }, 0)
  }

  const handleDropdownSelect = (tagText: string) => {
    if (tags.includes(tagText)) {
      setDuplicateError(`"${tagText}" is already added`)
      // Clear error after 3 seconds
      setTimeout(() => setDuplicateError(null), 3000)
      setInputValue("")
      setShowDropdown(false)
      skipDropdownOpenRef.current = true // Prevent dropdown from reopening on focus
      inputRef.current?.focus()
      return
    }
    onAddTag(tagText)
    setInputValue("")
    setShowDropdown(false)
    skipDropdownOpenRef.current = true // Prevent dropdown from reopening on focus
    inputRef.current?.focus()
  }

  const handleDropdownClose = () => {
    setShowDropdown(false)
    inputRef.current?.focus()
  }

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  return (
    <div className="relative flex flex-col gap-1.25" ref={containerRef}>
      <Label
        htmlFor={id}
        className={cn(
          "text-xs leading-5 uppercase",
          error ? "text-brand-terracotta-bright" : "text-body",
        )}
      >
        {label}
        {required && (
          <span className={error ? "text-brand-terracotta-bright" : "text-body"}>*</span>
        )}
      </Label>

      <div
        className={cn(
          "flex gap-2 items-center flex-wrap pb-2 border-b transition-all duration-200",
          error ? "border-brand-terracotta-bright" : "border-input",
        )}
      >
        {tags.map(tag => (
          <Badge
            key={tag}
            variant="secondary"
            className="gap-1.5 text-[13px] leading-5 p-2.5 pe-2 rounded-md border-white"
          >
            {tag}
            <button
              type="button"
              onClick={() => handleTagRemove(tag)}
              className="flex items-center justify-center rounded-full cursor-pointer text-white"
              aria-label={`Remove ${tag} tag`}
            >
              <X className="size-4" />
            </button>
          </Badge>
        ))}

        <div className="relative flex-1 min-w-[120px]">
          <input
            ref={inputRef}
            id={id}
            type="text"
            value={inputValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            placeholder={placeholder || "Type here.."}
            className={cn(
              "placeholder-text-body-ultra dark:placeholder-text-body-ultra w-full text-sm text-primary focus:outline-none bg-transparent",
              enableSearch && "pr-8",
            )}
            autoComplete="off"
          />

          {enableSearch && (
            <button
              type="button"
              onClick={() => {
                if (showDropdown) {
                  setShowDropdown(false)
                } else {
                  inputRef.current?.focus()
                }
              }}
              className="absolute right-0 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Toggle search"
            >
              <ChevronDown className="size-4" />
            </button>
          )}
        </div>
      </div>

      {error && <p className="text-xs text-brand-terracotta-bright">{error}</p>}
      {duplicateError && <p className="text-xs text-brand-terracotta-bright">{duplicateError}</p>}

      {enableSearch && showDropdown && (
        <TagSearchDropdown
          inputValue={inputValue}
          onSelect={handleDropdownSelect}
          onClose={handleDropdownClose}
          inputRef={inputRef}
          excludeIds={excludeIds.current}
          containerRef={containerRef}
        />
      )}
    </div>
  )
}
